import { Injectable } from '@angular/core';
import { LocalStorageService } from './local-storage.service';
import { Trip } from './trip.model';
import { FishingEvent } from './fishing-event.model';
import { TrawlSet } from './trawl-set.model';
import { TrawlTrip } from './trawl-trip.model';

/**
 * This service resolves conflicts from CouchDB/PouchDB syncing on {@link LocalStorageService.user_storage} manually.
 */
@Injectable({
  providedIn: 'root'
})
export class ConflictResolverService {

  constructor() { }

  /**
   * Resolve conflicts on user documents using merging and a last write wins strategy.
   * @param user_storage {@link LocalStorageService.user_storage} for the given user.
   * @returns Promise resolving on completion.
   * 
   * @remarks
   * We get all documents and for each one check if it is in a conflicted state.
   * Sets, Gears, completed landings, delivery buyers and permit numbers can be merged.
   * All others solved through 'last-write-wins', the most recent edit is chosen.
   * PouchDB lets us fetch conflicted versions for documents letting us see all the data.
   */
  static ResolveConflicts(user_storage: any) {
    console.log("Resolving conflicts")
    let num_conflicts = 0;

    return new Promise<number>((rs, rj) => {
      user_storage.allDocs({include_docs: true, conflicts: true}).then((docs: any) => {

        //Need a chained promise for the duration of the for loop
        let res_promise: Promise<number> = new Promise<number>((rs1, rj1) => rs1(0));
        for (var d=0; d<=docs.rows.length; d++) {
          res_promise = res_promise.then((next: number) => {
            if (next == docs.rows.length) {
              rs(num_conflicts);
              return 0;
            } else {  
              let curr_doc = docs.rows[next];         
              if (curr_doc.doc._conflicts) {
                num_conflicts++;
                switch (curr_doc.id) {
                  case 'delivery_buyers':
                  case 'permit_numbers':
                  case 'user_gear_list':
                  case 'user_landings_received':
                  case 'hms_drafts':                  
                  case 'trawl_landings_received':            
                    //merged list returned, update current winner with this list
                    return new Promise<number>((mRes, mRej) => {
                      this.ResolveByMerge(user_storage, curr_doc, curr_doc.id != 'delivery_buyers' && curr_doc.id != 'permit_numbers').then((list: string[]) => {
                        curr_doc.doc.list = list;
                        user_storage.put(curr_doc.doc).then((res: any) => mRes(next+1));
                      }).catch((err: any) => {
                        console.log(err);
                        mRes(next+1);
                      });
                    });
                  case 'user_doc':  
                  case 'logbook_edited':   
                  case 'trawl_logbook_edited':     
                    //winning doc returned, place it on top of current winner
                    return new Promise<number>((lwRes, lwRej) => {
                      this.ResolveByLastWriteWins(user_storage, curr_doc).then((rev_doc: any) => {
                        curr_doc.doc.lastUpdate = new Date();
                        if (curr_doc.id == 'user_doc') curr_doc.doc.userInfo = rev_doc.userInfo; else {
                          curr_doc.doc.groundFish = rev_doc.groundFish;
                          curr_doc.doc.HMS = rev_doc.HMS;
                        } 
                        user_storage.put(curr_doc.doc).then(() => lwRes(next+1)).catch((err: any) => {
                          console.log(err)
                          lwRes(next+1); 
                        });
                      }).catch((err: any) => {
                        console.log(err);
                        lwRes(next+1); 
                      });
                    });
                  case 'trip_pending':
                  case 'trawl_trip':
                    return new Promise<number>((tRes, tRej) => {
                      this.resolveOnTrip(user_storage, curr_doc).then((rev_doc: any) => {
                        console.log(rev_doc)
                        curr_doc.doc.lastUpdate = new Date();
                        curr_doc.doc.trip = rev_doc.trip;
                        delete curr_doc.doc._conflicts;
                        console.log(curr_doc)
                        user_storage.put(curr_doc.doc).then(() => tRes(next+1)).catch((err: any) => {                         
                          console.log(err)
                          tRes(next+1); 
                        });
                      }).catch((err: any) => {
                        console.log(err);
                        tRes(next+1); 
                      });
                    });
      
                  //A specific set, gear or completed delivery document
                  default:
                    //winning doc returned, place it on top of current winner
                    return new Promise<number>((lwRes, lwRej) => {
                      this.ResolveByLastWriteWins(user_storage, curr_doc).then((rev_doc: any) => {
                        //Sets or gears
                        if ((curr_doc.id as string).substring(0, 3) == 'set') this.TransliterateSetDocument(rev_doc, curr_doc.doc);
                        else curr_doc.doc.GearDetails = rev_doc.GearDetails; 
                        curr_doc.doc.lastUpdate = new Date();              
                        user_storage.put(curr_doc.doc).then(() => lwRes(next+1));
                      }).catch((err: any) => {
                        console.log(err);
                        lwRes(next+1);
                      });
                    })
                }  
              } else {
                return next+1;
              }                       
            }
          }).catch((err: any) => {
            console.log("Conflict resolution failed");
            console.log(err);
            rs(0);
            return 0;
          });                              
        }       
      }).catch((err: any) => {
        console.log("Conflict resolution failed");
        console.log(err);
        rs(0);
      });
    })
  }


  /**
   * Resolve on the trip. We must do a merge resolution on the sets, and a LWW resolution on the delivery.
   * @param user_storage PouchDB user storage
   * @param tripDoc The document in conflict
   * @returns Promise resolving on completion
   */
  static resolveOnTrip(user_storage: any, tripDoc: any) {
    return new Promise<string>((rs, rj) => {
      console.log(tripDoc);
      user_storage.get(tripDoc.id, {open_revs: tripDoc.doc._conflicts}).then((rev_doc: any[]) => {

        let lastTrip = JSON.parse(JSON.stringify(rev_doc.reduce((acc, curr) => 
          new Date(acc.ok.lastUpdate ?? 0).getTime() > new Date(curr.ok.lastUpdate ?? 0).getTime() ? acc : curr)));
        lastTrip = new Date(lastTrip.ok.lastUpdate ?? 0).getTime() > new Date(tripDoc.doc.lastUpdate ?? 0).getTime() ? lastTrip.ok : tripDoc.doc;
        let combined_sets = new Map((lastTrip.trip.sets as FishingEvent[] | TrawlSet[]).map(s => [s.fishingEventID, s]));

        rev_doc.forEach((idoc: any) => (idoc.ok.trip as Trip | TrawlTrip).sets.forEach(s => {
          if (!combined_sets.has(s.fishingEventID)) combined_sets.set(s.fishingEventID, s)}));

        tripDoc.doc.trip.sets.forEach((s: FishingEvent | TrawlSet) => {
          if (!combined_sets.has(s.fishingEventID)) combined_sets.set(s.fishingEventID, s)});

        //Sets of the winning trip start in this map
        lastTrip.trip.sets = [...combined_sets.values()];
        this.RemoveLosingRevisions(user_storage, tripDoc.id, tripDoc.doc._conflicts).then(() => rs(lastTrip));
      });
    });
  }


  /**
   * Get all non-deleted set/gear.. documents and merge them all together.
   * @param user_storage PouchDB user storage
   * @param doc The document in conflict
   * @param checkDoc Check if documents have been removed entirely
   * @returns Promise resolving on completion
   */
  static ResolveByMerge(user_storage: any, doc: any, checkDoc: boolean) {
    return new Promise<string[]>((rs, rj) => {
      console.log("Resolving for " + doc.id);
      let combined_list: string[] = doc.doc.list;

      user_storage.get(doc.id, {open_revs: doc.doc._conflicts}).then((rev_doc: any) => {
        for (var idoc of rev_doc) for (var setId of idoc.ok.list) if (!combined_list.includes(setId)) combined_list.push(setId);
        if (checkDoc) {
          user_storage.allDocs({keys: combined_list, deleted: 'ok'}).then((setDocs: any) => {
            combined_list = combined_list.filter((_,i) => setDocs.rows[i].value && !setDocs.rows[i].value.deleted)
            if (doc.doc._conflicts) this.RemoveLosingRevisions(user_storage, doc.id, doc.doc._conflicts).then(_ => rs(combined_list));
            else rs(combined_list);
          }); 
        } else if (doc.doc._conflicts) this.RemoveLosingRevisions(user_storage, doc.id, doc.doc._conflicts).then(_ => rs(combined_list));
          else rs(combined_list);    
      })      
    });
  }


  /**
   * All documents resolved with this method have a last updated timestamp.
   * Just pick the latest of these and select it as the winner.
   * @param user_storage PouchDB user storage
   * @param doc The document in conflict
   * @returns Promise resolving on completion
   */
  static ResolveByLastWriteWins(user_storage: any, doc: any) {
    return new Promise<string>((rs, rj) => {
      console.log("Resolving for " + doc.id);
      let accessToken: string = doc.id == 'user_doc' ? doc.doc.userInfo.accessToken : "";
      let refreshToken: string = doc.id == 'user_doc' ? doc.doc.userInfo.refreshToken : "";
      
      let winner: {rev: string, tm: Date, doc: any} = {rev: doc.doc._rev, tm: new Date(doc.doc.lastUpdate ?? 0), doc: doc.doc};
      user_storage.get(doc.id, {open_revs: doc.doc._conflicts, include_docs: true}).then((rev_doc: any) => {
        for (var idoc of rev_doc) {
          if (winner.tm.toISOString() < new Date(idoc.ok.lastUpdate ?? 0).toISOString()) winner = {rev: idoc.ok._rev, tm: new Date(idoc.ok.lastUpdate ?? 0), doc: idoc.ok};
        }

        if (doc.id == 'user_doc') {
          winner.doc.userInfo.accessToken = accessToken;
          winner.doc.userInfo.refreshToken = refreshToken;
        }
        this.RemoveLosingRevisions(user_storage, doc.id, doc.doc._conflicts).then(() => rs(winner.doc));
      }) 
    });
  }


  /**
   * To mark a conflict as resolved we have to remove all 'losing' revisions.
   * The two resolution methods in this class decide the winning doc, all other revisions are removed.
   * @param user_storage PouchDB user storage
   * @param doc_id id of doc to remove revisions from
   * @param revs Revisions of doc to remove
   * @returns Promise resolving on completion
   */
  static RemoveLosingRevisions(user_storage: any, doc_id: string, revs: string[]) {
    return new Promise<boolean>((resolve, reject) => {
      //Start the promise chain with removing the first losing revision in revs
      let rev_promise: Promise<number> = new Promise<number>((revRes, revRej) => revRes(0));

      //Chain promises: if the end of revs is reached resolve the entire outer promise and we're done
      //If not, take the passed value and return a promise that resolves with value+1 when the revision at revs[value] has been removed
      for (var r=0; r<=revs.length; r++) {
        rev_promise = rev_promise.then((next: number) => {
          return new Promise((revRes, revRej) => {
            if (next == revs.length) {
              console.log("Removed all revisions");
              resolve(true);
            } 
            else {
              user_storage.remove(doc_id, revs[next]).then(() => {
                console.log("Removed revision " + revs[next]);
                revRes(next+1);
              }).catch((err: any) => console.log(err));
            }         
          });                    
        })
      }     
    });
  }


  static TransliterateSetDocument(from_doc: any, to_doc: any) {
    to_doc.startDate = from_doc.startDate
    to_doc.startLat = from_doc.startLat
    to_doc.startLon = from_doc.startLon
    to_doc.startDepth = from_doc.startDepth
    to_doc.depthOffBottom = from_doc.depthOffBottom
    to_doc.endDate = from_doc.endDate
    to_doc.endLat = from_doc.endLat
    to_doc.endLon = from_doc.endLon
    to_doc.endDepth = from_doc.endDepth
    if (to_doc.gear) to_doc.gear.gearID = from_doc.gear ? from_doc.gear.gearID : to_doc.gear.gearID;
    to_doc.setNumber = from_doc.setNumber
    to_doc.gearPerSet = from_doc.gearPerSet
    to_doc.skateNum = from_doc.skateNum
    to_doc.lostPotCount = from_doc.lostPotCount
    to_doc.catchItems = from_doc.catchItems
    to_doc.startLatManualEntry = from_doc.startLatManualEntry
    to_doc.startLonManualEntry = from_doc.startLonManualEntry
    to_doc.endLatManualEntry = from_doc.endLatManualEntry
    to_doc.endLonManualEntry = from_doc.endLonManualEntry
    to_doc.comments = from_doc.comments;
    to_doc.baitType = from_doc.baitType;
    return to_doc;
  }
}